import { create } from 'zustand';
import { persist } from 'zustand/middleware';

// Session timeout in milliseconds (5 minutes default)
const SESSION_TIMEOUT = 5 * 60 * 1000; // 5 minutes
const WARNING_TIME = 1 * 60 * 1000; // 1 minute before expiry

const useUserStore = create(
  persist(
    (set, get) => ({
      user: null,
      isAuthorized: false,
      lastActivity: null,
      sessionTimeout: SESSION_TIMEOUT,
      warningShown: false,
      sessionTimer: null,
      warningTimer: null,
      
      setUser: (userData) => {
        console.log('Setting user data:', userData);
        const now = Date.now();
        set({ 
          user: userData, 
          isAuthorized: true,
          lastActivity: now,
          warningShown: false
        });
        
        // Start session monitoring
        get().startSessionMonitoring();
      },
      
      clearUser: () => {
        console.log('Clearing user data');
        get().stopSessionMonitoring();
        set({ 
          user: null, 
          isAuthorized: false,
          lastActivity: null,
          warningShown: false
        });
      },
      
      logout: () => {
        console.log('User logging out...');
        get().stopSessionMonitoring();
        set({ 
          user: null, 
          isAuthorized: false,
          lastActivity: null,
          warningShown: false
        });
        localStorage.clear();
        window.location.href = '/login';
      },
      
      // Update last activity timestamp
      updateActivity: () => {
        const state = get();
        if (state.isAuthorized) {
          const now = Date.now();
          console.log('Activity updated:', new Date(now).toLocaleTimeString());
          set({ 
            lastActivity: now,
            warningShown: false
          });
          
          // Restart session monitoring with new activity time
          state.startSessionMonitoring();
        }
      },
      
      // Check if session is expired
      isSessionExpired: () => {
        const state = get();
        if (!state.isAuthorized || !state.lastActivity) return false;
        
        const now = Date.now();
        const timeSinceActivity = now - state.lastActivity;
        return timeSinceActivity > state.sessionTimeout;
      },
      
      // Get time remaining until session expires
      getTimeUntilExpiry: () => {
        const state = get();
        if (!state.isAuthorized || !state.lastActivity) return 0;
        
        const now = Date.now();
        const timeSinceActivity = now - state.lastActivity;
        const timeRemaining = state.sessionTimeout - timeSinceActivity;
        return Math.max(0, timeRemaining);
      },
      
      // Start session monitoring
      startSessionMonitoring: () => {
        const state = get();
        
        // Clear existing timers
        state.stopSessionMonitoring();
        
        // Set warning timer
        const warningTimer = setTimeout(() => {
          const currentState = get();
          if (currentState.isAuthorized && !currentState.warningShown) {
            console.log('Session warning: expiring soon');
            set({ warningShown: true });
            
            // Dispatch custom event for warning
            window.dispatchEvent(new CustomEvent('sessionWarning', {
              detail: { timeRemaining: WARNING_TIME }
            }));
          }
        }, state.sessionTimeout - WARNING_TIME);
        
        // Set expiry timer
        const sessionTimer = setTimeout(() => {
          const currentState = get();
          if (currentState.isAuthorized) {
            console.log('Session expired due to inactivity');
            
            // Dispatch custom event for expiry
            window.dispatchEvent(new CustomEvent('sessionExpired'));
            
            // Auto logout
            currentState.logout();
          }
        }, state.sessionTimeout);
        
        set({ sessionTimer, warningTimer });
      },
      
      // Stop session monitoring
      stopSessionMonitoring: () => {
        const state = get();
        if (state.sessionTimer) {
          clearTimeout(state.sessionTimer);
        }
        if (state.warningTimer) {
          clearTimeout(state.warningTimer);
        }
        set({ sessionTimer: null, warningTimer: null });
      },
      
      // Extend session (reset activity)
      extendSession: () => {
        console.log('Session extended by user');
        get().updateActivity();
      },
      
      // Set custom session timeout
      setSessionTimeout: (timeout) => {
        set({ sessionTimeout: timeout });
        if (get().isAuthorized) {
          get().startSessionMonitoring();
        }
      },
      
      // Functions to get current state
      getUser: () => get().user,
      getIsAuthorized: () => get().isAuthorized,
      getCurrentState: () => ({
        user: get().user,
        isAuthorized: get().isAuthorized,
        lastActivity: get().lastActivity,
        timeUntilExpiry: get().getTimeUntilExpiry()
      }),
      
      // Helper function to check if user has specific permissions/roles
      hasRole: (role) => {
        const user = get().user;
        return user && user.roles && user.roles.includes(role);
      },
      
      // Helper function to check if user session is still valid
      isSessionValid: () => {
        const state = get();
        if (!state.user || !state.isAuthorized) return false;
        
        // Check if session is expired due to inactivity
        if (state.isSessionExpired()) {
          console.log('Session invalid: expired due to inactivity');
          state.logout();
          return false;
        }
        
        // Add token expiry check if you have token expiration
        if (state.user.tokenExpiry) {
          const now = new Date().getTime();
          if (now >= state.user.tokenExpiry) {
            console.log('Session invalid: token expired');
            state.logout();
            return false;
          }
        }
        
        return true;
      }
    }),
    {
      name: 'user-storage',
      // Persist user, isAuthorized, and lastActivity
      partialize: (state) => ({ 
        user: state.user, 
        isAuthorized: state.isAuthorized,
        lastActivity: state.lastActivity,
        sessionTimeout: state.sessionTimeout
      }),
      // Restore session monitoring on hydration
      onRehydrateStorage: () => (state) => {
        if (state && state.isAuthorized) {
          // Check if session expired while app was closed
          if (state.isSessionExpired()) {
            console.log('Session expired while app was closed');
            state.clearUser();
          } else {
            // Restart session monitoring
            state.startSessionMonitoring();
            console.log('Session monitoring restored');
            console.log("user data:", state.user);
          }
        }
      }
    }
  )
);

// Export functions for use outside React components
export const getUserStore = () => useUserStore.getState();
export const getUser = () => useUserStore.getState().getUser();
export const getIsAuthorized = () => useUserStore.getState().getIsAuthorized();
export const getCurrentUserState = () => useUserStore.getState().getCurrentState();
export const updateUserActivity = () => useUserStore.getState().updateActivity();

export default useUserStore;